import type { ApolloClient } from '@apollo/client'

import { publicApiClient } from '~publish/clients/publicApiClient'
import {
  GUEST_MEDIA_MAX_COUNT,
  GUEST_MEDIA_REQUIREMENTS_MESSAGE,
  GUEST_MEDIA_TTL_MS,
  GuestMediaSchema,
  getGuestMediaMaxFileSizeBytes,
  isGuestMediaMimeType,
} from '~publish/helpers/guestMedia'
import type { GuestMedia } from '~publish/helpers/guestMedia'

import {
  AnonymousMediaAccess,
  GenerateAnonymousMediaUploadForm,
} from './graphql/anonymousMediaOperations'
import { getGuestMediaTurnstileToken } from './guestMediaTurnstile'

type UploadGuestMediaInput = {
  file: File
  currentMediaIds: readonly string[]
  mediaId?: string
  signal?: AbortSignal
  onProgress?: (fraction: number) => void
}

type UploadFormOptions = {
  signal?: AbortSignal
  onProgress?: (fraction: number) => void
}

type GuestMediaClientDependencies = {
  client?: ApolloClient
  postUploadForm?: typeof postGuestMediaUploadForm
  getTurnstileToken?: typeof getGuestMediaTurnstileToken
}

type GuestMediaAccessOptions = {
  client?: ApolloClient
  download?: boolean
  signal?: AbortSignal
}

export type GuestMediaAccess = Readonly<{
  accessUrl: string
  assetExpiresAt: number
}>

function getPayloadError(
  payload: { __typename?: string; message?: string } | null | undefined,
  fallbackMessage: string,
): Error {
  return new Error(payload?.message ?? fallbackMessage)
}

export function postGuestMediaUploadForm(
  url: string,
  body: FormData,
  { signal, onProgress }: UploadFormOptions = {},
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('The upload was aborted', 'AbortError'))
      return
    }

    const request = new XMLHttpRequest()
    const abort = (): void => request.abort()

    request.upload.addEventListener('progress', (event): void => {
      if (event.lengthComputable && event.total > 0) {
        onProgress?.(event.loaded / event.total)
      }
    })
    request.addEventListener('loadend', (): void => {
      signal?.removeEventListener('abort', abort)
    })
    request.addEventListener('load', (): void => {
      resolve(request.status >= 200 && request.status < 300)
    })
    request.addEventListener('error', (): void => {
      reject(new Error('The image upload failed'))
    })
    request.addEventListener('abort', (): void => {
      reject(new DOMException('The upload was aborted', 'AbortError'))
    })

    signal?.addEventListener('abort', abort)
    request.open('POST', url)
    request.withCredentials = false
    request.send(body)
  })
}

async function readImageDimensions(
  file: File,
): Promise<{ width: number; height: number } | undefined> {
  if (typeof createImageBitmap !== 'function') return undefined

  try {
    const bitmap = await createImageBitmap(file)
    const dimensions = { width: bitmap.width, height: bitmap.height }
    bitmap.close()
    return dimensions.width > 0 && dimensions.height > 0
      ? dimensions
      : undefined
  } catch {
    return undefined
  }
}

export async function uploadGuestMedia(
  { file, currentMediaIds, mediaId, signal, onProgress }: UploadGuestMediaInput,
  {
    client = publicApiClient,
    postUploadForm = postGuestMediaUploadForm,
    getTurnstileToken = getGuestMediaTurnstileToken,
  }: GuestMediaClientDependencies = {},
): Promise<GuestMedia> {
  const uniqueMediaIds = new Set(currentMediaIds)
  if (uniqueMediaIds.size !== currentMediaIds.length) {
    throw new Error('The current image list is invalid')
  }

  if (
    currentMediaIds.length > GUEST_MEDIA_MAX_COUNT ||
    (currentMediaIds.length === GUEST_MEDIA_MAX_COUNT && mediaId === undefined)
  ) {
    throw new Error(`A draft can contain up to ${GUEST_MEDIA_MAX_COUNT} files`)
  }

  if (mediaId !== undefined && !uniqueMediaIds.has(mediaId)) {
    throw new Error('The image to replace could not be found')
  }

  if (!isGuestMediaMimeType(file.type)) {
    throw new Error('Select a JPEG, PNG, GIF, or WebP image')
  }

  if (!file.name.trim() || file.size <= 0) {
    throw new Error('Select a non-empty image file')
  }

  if (file.size > getGuestMediaMaxFileSizeBytes(file.type)) {
    throw new Error(GUEST_MEDIA_REQUIREMENTS_MESSAGE)
  }

  const [turnstileToken, dimensions] = await Promise.all([
    getTurnstileToken(signal),
    readImageDimensions(file),
  ])

  const { data } = await client.mutate({
    mutation: GenerateAnonymousMediaUploadForm,
    variables: {
      input: {
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        turnstileToken,
      },
    },
    fetchPolicy: 'no-cache',
    context: signal ? { fetchOptions: { signal } } : undefined,
  })
  const payload = data?.generateAnonymousMediaUploadForm

  if (payload?.__typename !== 'GenerateAnonymousMediaUploadFormSuccess') {
    throw getPayloadError(payload, 'The image upload could not start')
  }

  const expiresAt = Date.parse(payload.expiresAt)
  const media = GuestMediaSchema.parse({
    id: mediaId ?? crypto.randomUUID(),
    assetUrl: payload.assetUrl,
    fileName: file.name,
    mimeType: file.type,
    size: file.size,
    uploadedAt: expiresAt - GUEST_MEDIA_TTL_MS,
    expiresAt,
    ...dimensions,
  })

  const body = new FormData()
  for (const field of payload.uploadFields) {
    body.append(field.name, field.value)
  }
  body.append('file', file)

  const uploaded = await postUploadForm(payload.uploadUrl, body, {
    signal,
    onProgress,
  })

  if (!uploaded) {
    throw new Error('The image upload failed')
  }

  return Object.freeze(media)
}

export async function getGuestMediaAccess(
  media: GuestMedia,
  {
    client = publicApiClient,
    download = false,
    signal,
  }: GuestMediaAccessOptions = {},
): Promise<GuestMediaAccess> {
  const { data } = await client.query({
    query: AnonymousMediaAccess,
    variables: {
      input: {
        assetUrl: media.assetUrl,
        ...(download ? { downloadFileName: media.fileName } : {}),
      },
    },
    fetchPolicy: 'no-cache',
    context: {
      queryDeduplication: false,
      ...(signal ? { fetchOptions: { signal } } : {}),
    },
  })
  const payload = data?.anonymousMediaAccess

  if (payload?.__typename !== 'AnonymousMediaAccessSuccess') {
    throw getPayloadError(payload, 'The image could not be loaded')
  }

  const assetExpiresAt = Date.parse(payload.assetExpiresAt)
  if (!Number.isFinite(assetExpiresAt)) {
    throw new Error('The image expiry time is invalid')
  }

  return Object.freeze({
    accessUrl: payload.accessUrl,
    assetExpiresAt,
  })
}
